import { forwardRef, useCallback, useId } from "react";
import { useAtomValue, type PrimitiveAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { Subject } from "gitpod-next-api/gitpod/v1/identity_pb";
import { InputField } from "@/components/podkit/forms/InputField";
import { Text } from "@/components/podkit/typography/Text";
import { Button } from "@/components/flexkit/Button";
import type { PlainWorkflow } from "@/queries/automation-queries";
import { WorkflowEditAction } from "@/components/automations/AutomationEditAction";
import { WorkflowEditTriggers } from "@/components/automations/AutomationEditTriggers";
import { WorkflowEditExecutor } from "@/components/automations/AutomationEditExecutor";
import {
    type WorkflowEditFormTriggerData,
    type WorkflowEditFormLimitsData,
} from "@/components/automations/automation-edit-form-data";
import { cn } from "@/components/podkit/lib/cn";

type AutomationTriggerEditAreaProps = {
    workflow: PlainWorkflow;
    executorAtom: PrimitiveAtom<PlainMessage<Subject> | undefined>;
    triggersAtom: PrimitiveAtom<WorkflowEditFormTriggerData[]>;
    limitsAtom: PrimitiveAtom<WorkflowEditFormLimitsData | undefined>;
    onClose: () => void;
    className?: string;
};

export const AutomationTriggerEditArea = forwardRef<HTMLDivElement, AutomationTriggerEditAreaProps>(
    ({ workflow, executorAtom, triggersAtom, limitsAtom, onClose, className }, ref) => {
        const executorId = useId();

        // Read executor for the executor selector - writes go directly to parent atom
        const executor = useAtomValue(executorAtom);

        const handleExecutorChange = useAtomCallback(
            useCallback(
                (_get, set, newExecutor: PlainMessage<Subject> | undefined) => {
                    set(executorAtom, newExecutor);
                },
                [executorAtom],
            ),
        );

        return (
            <div ref={ref} className={cn("relative flex flex-1 grow flex-col overflow-auto", className)}>
                <Text className="p-6 text-lg font-medium">Edit trigger</Text>
                <div className="flex flex-1 flex-col gap-6 bg-surface-primary px-6">
                    <WorkflowEditTriggers workflow={workflow} triggersAtom={triggersAtom} />
                    <WorkflowEditAction limitsAtom={limitsAtom} />
                    <InputField className="w-1/2" label="Run Automation as" id={executorId}>
                        <WorkflowEditExecutor
                            selectedExecutorId={executor?.id}
                            onExecutorChange={handleExecutorChange}
                            className="w-full"
                        />
                    </InputField>
                </div>
                <div className="flex justify-end gap-2 border-t border-border-subtle py-4 pl-4 pr-6">
                    <Button variant="outline" onClick={onClose} data-tracking-id="close">
                        Close
                    </Button>
                </div>
            </div>
        );
    },
);

AutomationTriggerEditArea.displayName = "AutomationTriggerEditArea";
